// UI der KI-Plancheck-PWA — Port von templates/index.html (Flask).
// Unterschiede zum Original: Server-Routen → Worker-RPC (mupdf-Pipeline lokal),
// Bilder/Roh-PDFs in IndexedDB (überleben Reload inkl. Bild), Feedback → D1,
// PDF-Export client-seitig (pdf-lib), Login-Gate (Cloudflare-Worker-Session).
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const SEV: Record<string, string> = { hoch: "#e5484d", mittel: "#f5a623", gering: "#4c6ef5" };
const CHECKLIST_URL = "https://planpruefung-checkliste.pages.dev";

interface Box { x: number; y: number; w: number; h: number }
interface Mangel {
  nummer?: number; katalog_nr?: number; gewerk?: string; raumnummer?: string; ort?: string;
  anker?: string; titel?: string; norm?: string; schwere?: string; konfidenz?: string;
  klasse?: string; beschreibung?: string; box?: Box | null; bewertung?: string;
  rueckmeldung?: string; plaene?: string[];
}
interface Room { raumnummer: string | null; raumname: string | null; raumtyp: string | null; poolar_id: string; [k: string]: unknown }
interface PlanState {
  key?: string; page?: string; plan_name: string; width: number; height: number;
  maengel: Mangel[]; rooms: Room[]; model?: string | null; custom_id?: string;
  batchPending?: boolean; filename?: string;
  image_url?: string | null; // Laufzeit-Blob-URL, nicht persistiert
}

let state: PlanState = { plan_name: "", width: 0, height: 0, maengel: [], rooms: [], image_url: null };
let view = { scale: 1, tx: 0, ty: 0 };
let planeW = 0, planeH = 0;
let activeNum: number | null = null;
let plans: PlanState[] = [];
let batchId: string | null = null;
let batchTimer: ReturnType<typeof setTimeout> | undefined;

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const esc = (s: unknown) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
const hexA = (hex: string, a: number) => { const n = parseInt(hex.slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`; };
function msg(text: string, cls: string) { $("messages").innerHTML = text ? `<div class="msg ${cls}">${text}</div>` : ""; }

/* ---------- Worker-RPC ---------- */
const worker = new Worker(new URL("./pdf.worker.ts", import.meta.url), { type: "module" });
let rpcId = 1;
const pending = new Map<number, (r: Record<string, unknown>) => void>();
worker.onmessage = (ev) => {
  const r = ev.data;
  if (r.ready) return;
  pending.get(r.id)?.(r);
  pending.delete(r.id);
};
function rpc<T = Record<string, unknown>>(op: string, args: Record<string, unknown>, transfer: Transferable[] = []): Promise<T> {
  const id = rpcId++;
  return new Promise((resolve, reject) => {
    pending.set(id, (r) => (r.ok ? resolve(r as T) : reject(new Error(String(r.error)))));
    worker.postMessage({ id, op, ...args }, transfer);
  });
}

/* ---------- IndexedDB: Bilder + Roh-PDFs je Plan (überleben Reload) ---------- */
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open("plancheck", 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of ["inventory", "batch", "plans"]) if (!db.objectStoreNames.contains(s)) db.createObjectStore(s);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
let dbP: Promise<IDBDatabase> | null = null;
const getDb = () => (dbP ??= openDb());
async function idbPut(key: string, val: unknown) {
  const db = await getDb();
  return new Promise<void>((res, rej) => {
    const tx = db.transaction("plans", "readwrite");
    tx.objectStore("plans").put(val, key);
    tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error);
  });
}
async function idbGet<T>(key: string): Promise<T | null> {
  const db = await getDb();
  return new Promise((res, rej) => {
    const rq = db.transaction("plans", "readonly").objectStore("plans").get(key);
    rq.onsuccess = () => res((rq.result as T) ?? null); rq.onerror = () => rej(rq.error);
  });
}
async function idbDel(key: string) {
  const db = await getDb();
  return new Promise<void>((res) => {
    const tx = db.transaction("plans", "readwrite");
    tx.objectStore("plans").delete(key);
    tx.oncomplete = () => res();
  });
}
interface PlanBlobs { disp?: Blob; raw?: Blob; filename?: string }
const blobKey = (key: string) => `blobs:${key}`;
async function savePlanBlobs(key: string, blobs: PlanBlobs) {
  const prev = (await idbGet<PlanBlobs>(blobKey(key))) ?? {};
  await idbPut(blobKey(key), { ...prev, ...blobs });
}

/* ---------- Login ---------- */
async function ensureLogin(): Promise<void> {
  const ok = (await fetch("/api/me")).ok;
  const ov = $("login-ov");
  ov.hidden = ok;
  $("logout").hidden = !ok;
  if (ok) return;
  await new Promise<void>((resolve) => {
    $("loginform").onsubmit = async (e) => {
      e.preventDefault();
      const r = await fetch("/api/login", { method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ password: ($("pw") as HTMLInputElement).value }) });
      if (r.ok) { ov.hidden = true; $("logout").hidden = false; resolve(); }
      else $("loginstatus").textContent = "Falsches Passwort";
    };
  });
}
$("logout").onclick = async () => { await fetch("/api/logout", { method: "POST" }); location.reload(); };

/* ---------- Multi-Plan ---------- */
const curPage = () => ($("page") as HTMLInputElement).value || "0";
const planKey = (name: string, page: string) => (name || "?") + "#" + (page || "0");

const PERSIST_KEY = "plancheck_session_v2";
let persistTimer: ReturnType<typeof setTimeout> | undefined;
function persist() {
  clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      if (!plans.length) { localStorage.removeItem(PERSIST_KEY); return; }
      const slim = plans.map((p) => ({ ...p, image_url: undefined }));
      localStorage.setItem(PERSIST_KEY, JSON.stringify({ plans: slim, activeKey: state.key || null, batchId, ts: Date.now() }));
    } catch { /* Quota/Privatmodus */ }
  }, 300);
}
async function restore() {
  let saved: { plans: PlanState[]; activeKey: string | null; batchId: string | null } | null = null;
  try { saved = JSON.parse(localStorage.getItem(PERSIST_KEY) || "null"); } catch { return; }
  if (!saved || !Array.isArray(saved.plans) || !saved.plans.length) return;
  plans = saved.plans;
  for (const p of plans) {                          // Bilder aus IndexedDB zurückholen
    const blobs = await idbGet<PlanBlobs>(blobKey(p.key ?? ""));
    if (blobs?.disp) p.image_url = URL.createObjectURL(blobs.disp);
    if (blobs?.filename) p.filename = blobs.filename;
  }
  state = plans.find((p) => p.key === saved!.activeKey) || plans[0];
  activeNum = null;
  if (state.page != null) ($("page") as HTMLInputElement).value = state.page;
  render();
  const pend = plans.some((p) => p.batchPending);
  if (saved.batchId && pend) { batchId = saved.batchId; void pollBatch(); }
  msg(`Sitzung wiederhergestellt · ${plans.length} Plan/Pläne` + (pend ? " · Batch läuft weiter" : ""), "warn");
}
function upsertActivePlan() {
  state.key = planKey(state.plan_name, curPage());
  state.page = curPage();
  const i = plans.findIndex((p) => p.key === state.key);
  if (i >= 0) plans[i] = state; else plans.push(state);
}
function switchPlan(key: string) {
  const p = plans.find((p) => p.key === key);
  if (!p || p.key === state.key) return;
  state = p; activeNum = null;
  if (p.page != null) ($("page") as HTMLInputElement).value = p.page;
  render();
}
function closePlan(key: string, ev: Event) {
  ev.stopPropagation();
  const i = plans.findIndex((p) => p.key === key);
  if (i < 0) return;
  void idbDel(blobKey(key));
  const wasActive = plans[i].key === state.key;
  plans.splice(i, 1);
  if (!plans.length) { try { localStorage.removeItem(PERSIST_KEY); } catch { /* egal */ } location.reload(); return; }
  if (wasActive) { state = plans[Math.max(0, i - 1)]; activeNum = null;
    if (state.page != null) ($("page") as HTMLInputElement).value = state.page; render(); }
  else renderTabs();
}
function renderTabs() {
  const box = $("plantabs");
  if (plans.length <= 1) { box.style.display = "none"; box.innerHTML = ""; return; }
  box.style.display = "";
  box.innerHTML = plans.map((p) => {
    const active = p.key === state.key;
    const n = (p.maengel || []).length;
    const dot = (p.model || "").includes("opus") ? "#7c3aed" : (p.model ? "#0e9f9a" : "#c7c9d1");
    const nm = esc(p.plan_name || "Plan");
    const short = (p.plan_name || "Plan").length > 26 ? nm.slice(0, 24) + "…" : nm;
    const pend = p.batchPending ? "⏳ " : "";
    return `<div class="ptab${active ? " active" : ""}" data-k="${esc(p.key)}" title="${nm}">` +
      `<span class="mdl" style="background:${dot}" title="${esc(p.model || (p.batchPending ? "Batch läuft" : "noch nicht analysiert"))}"></span>` +
      `<span>${pend}${short}</span>` + (n ? `<span class="cnt">${n}</span>` : "") +
      `<button class="x" data-x="${esc(p.key)}" title="Schließen">✕</button></div>`;
  }).join("");
  box.querySelectorAll<HTMLElement>(".ptab").forEach((t) => (t.onclick = () => switchPlan(t.dataset.k!)));
  box.querySelectorAll<HTMLElement>("[data-x]").forEach((b) => (b.onclick = (e) => closePlan(b.dataset.x!, e)));
}

/* ---------- Analyse ---------- */
async function rawForState(): Promise<{ raw: ArrayBuffer; filename: string } | null> {
  const f = ($("file") as HTMLInputElement).files?.[0];
  const fStem = f ? f.name.replace(/\.[^.]+$/, "") : null;
  if (f && state.plan_name === fStem) return { raw: await f.arrayBuffer(), filename: f.name };
  const blobs = await idbGet<PlanBlobs>(blobKey(state.key ?? ""));
  if (blobs?.raw) return { raw: await blobs.raw.arrayBuffer(), filename: blobs.filename ?? state.plan_name + ".pdf" };
  if (f) return { raw: await f.arrayBuffer(), filename: f.name };
  return null;
}
async function analyze() {
  if (!state.plan_name && !($("file") as HTMLInputElement).files?.length) { msg("Bitte zuerst eine Datei wählen.", "err"); return; }
  const src = await rawForState();
  if (!src) { msg("Roh-Datei nicht mehr verfügbar — Plan bitte neu laden.", "err"); return; }
  msg("", ""); ($("go") as HTMLButtonElement).disabled = true;
  $("status").innerHTML = '<span class="spin"></span> Analysiere …';
  try {
    const d = await rpc<{ maengel: Mangel[]; model: string; width: number; height: number; truncated: boolean; inspections: number }>(
      "analyze", { raw: src.raw, filename: src.filename, page: parseInt(curPage()), mode: ($("modus") as HTMLSelectElement).value,
        rtCheck: ($("rtcheck") as HTMLInputElement).checked }, [src.raw]);
    if (d.truncated) msg("KI-Antwort war unvollständig (Token-Limit erreicht) – die Befundliste ist evtl. gekürzt. Plan ggf. im Modus \"streng\" erneut prüfen.", "warn");
    else if (d.inspections) msg(`Detail-Lupe: ${d.inspections} hochauflösende Ausschnitte geprüft.`, "");
    Object.assign(state, { width: d.width, height: d.height, maengel: d.maengel || [], model: d.model });
    upsertActivePlan();
    activeNum = null;
    render();
  } catch (e) {
    msg("KI-Analyse fehlgeschlagen: " + (e as Error).message, "err");
  } finally {
    ($("go") as HTMLButtonElement).disabled = false; $("status").textContent = "";
  }
}

/* ---------- Cross-Plan-Konsistenzprüfung (N2) ---------- */
async function crossCheck() {
  upsertActivePlan();
  const withRooms = plans.filter((p) => (p.rooms || []).length);
  if (!withRooms.length) {
    msg("Konsistenzprüfung braucht mindestens einen geladenen Plan mit erkannten Raumstempeln (verglichen wird gegen weitere geladene Pläne und die Projekt-DB).", "warn");
    return;
  }
  ($("crossgo") as HTMLButtonElement).disabled = true;
  $("status").innerHTML = '<span class="spin"></span> Konsistenzprüfung …';
  try {
    const d = await rpc<{ maengel: Mangel[]; plaene_geprueft: number; davon_db: number }>(
      "crosscheck", { useDb: true, plans: withRooms.map((p) => ({ plan_name: p.plan_name, rooms: p.rooms })) });
    for (const p of plans) p.maengel = (p.maengel || []).filter((m) => (m.gewerk || "").toLowerCase() !== "konsistenz");
    for (const m of d.maengel || []) {
      const names = (m.plaene || []).map((pn) => pn.replace(/ \[DB\]$/, ""));
      let targets = plans.filter((p) => names.includes(p.plan_name));
      if (!targets.length) targets = [state];
      for (const p of targets) p.maengel.push({ ...m, box: null });
    }
    for (const p of plans) p.maengel.forEach((m, i) => { m.nummer = i + 1; });
    const scope = `${d.plaene_geprueft} Pläne${d.davon_db ? ` (davon ${d.davon_db} aus der Projekt-DB)` : ""}`;
    msg((d.maengel || []).length
      ? `Konsistenzprüfung über ${scope}: ${d.maengel.length} Widersprüche gefunden (violett, den beteiligten Plänen zugeordnet).`
      : `Konsistenzprüfung über ${scope}: keine Widersprüche.`,
      (d.maengel || []).length ? "warn" : "");
    activeNum = null;
    render();
  } catch (e) { msg(String((e as Error).message), "err"); }
  finally { ($("crossgo") as HTMLButtonElement).disabled = false; $("status").textContent = ""; }
}

/* ---------- Batch-Analyse ---------- */
async function submitBatch() {
  const files = ($("batchfile") as HTMLInputElement).files;
  if (!files?.length) return;
  msg(`Batch wird vorbereitet (${files.length} Pläne) …`, "warn");
  ($("batchgo") as HTMLButtonElement).disabled = true;
  try {
    const payload = await Promise.all([...files].map(async (f) => ({ raw: await f.arrayBuffer(), filename: f.name })));
    const d = await rpc<{ batch_id: string; plans: { custom_id: string; plan_name: string; png: ArrayBuffer; width: number; height: number; rooms: Room[]; model: string; maengel: Mangel[] }[] }>(
      "batchSubmit", { files: payload, mode: ($("modus") as HTMLSelectElement).value, rtCheck: ($("rtcheck") as HTMLInputElement).checked },
      payload.map((p) => p.raw));
    batchId = d.batch_id;
    let first: PlanState | null = null;
    for (const [idx, p] of d.plans.entries()) {
      const dispBlob = new Blob([p.png], { type: "image/png" });
      const st: PlanState = { image_url: URL.createObjectURL(dispBlob), width: p.width, height: p.height,
        maengel: p.maengel || [], plan_name: p.plan_name, rooms: p.rooms || [], model: null,
        custom_id: p.custom_id, batchPending: true, key: planKey(p.plan_name, "0"), page: "0" };
      void savePlanBlobs(st.key!, { disp: dispBlob, raw: new Blob([payload[idx].raw ?? new ArrayBuffer(0)]), filename: p.plan_name + ".pdf" });
      const i = plans.findIndex((x) => x.key === st.key);
      if (i >= 0) plans[i] = st; else plans.push(st);
      if (!first) first = st;
    }
    if (first) { state = first; activeNum = null; render(); }
    msg(`Batch läuft · ${d.plans.length} Pläne eingereicht (−50 %). Ergebnisse kommen automatisch (meist < 1 h).`, "warn");
    void pollBatch();
  } catch (e) { msg("Batch-Fehler: " + (e as Error).message, "err"); }
  finally { ($("batchgo") as HTMLButtonElement).disabled = false; ($("batchfile") as HTMLInputElement).value = ""; }
}
async function pollBatch() {
  clearTimeout(batchTimer);
  if (!batchId) return;
  try {
    const d = await rpc<{ status: string; counts?: Record<string, number>; plans?: { custom_id: string; maengel: Mangel[]; failed: boolean; truncated: boolean }[] }>(
      "batchStatus", { batchId });
    if (d.status === "ended") {
      let n = 0, warn = 0;
      for (const p of d.plans ?? []) {
        const pl = plans.find((x) => x.custom_id === p.custom_id);
        if (pl) {
          const det = (pl.maengel || []).filter((m) => (m as { quelle?: string }).quelle === "deterministisch");
          pl.maengel = p.failed ? pl.maengel : [...det, ...(p.maengel || [])];
          pl.maengel.forEach((m, i) => { m.nummer = i + 1; });
          pl.batchPending = false; n++;
        }
        if (p.truncated || p.failed) warn++;
      }
      batchId = null; activeNum = null; render();
      msg(`Batch fertig · ${n} Pläne analysiert. Zwischen ihnen oben umschalten.` +
        (warn ? ` ⚠ ${warn} Plan/Pläne unvollständig oder fehlgeschlagen – bitte einzeln nachprüfen.` : ""), "warn");
    } else {
      const c = d.counts || {};
      msg(`Batch läuft … ${c.succeeded || 0} fertig · ${c.processing || 0} offen` +
        (c.errored ? ` · ${c.errored} Fehler` : "") + " (aktualisiert automatisch)", "warn");
      batchTimer = setTimeout(pollBatch, 45000);
    }
  } catch { batchTimer = setTimeout(pollBatch, 45000); }
}

/* ---------- Plan laden (Vorschau ohne Analyse) ---------- */
async function loadPlan() {
  const f = ($("file") as HTMLInputElement).files?.[0];
  if (!f) return;
  msg("", ""); $("status").innerHTML = '<span class="spin"></span> Lade Vorschau …';
  try {
    const raw = await f.arrayBuffer();
    const rawCopy = raw.slice(0);
    const d = await rpc<{ png: ArrayBuffer; width: number; height: number; plan_name: string; rooms: Room[]; maengel: Mangel[]; revision_info: { vorher: string; erfasst: string; aenderungen: string[] } | null; db_count: number }>(
      "preview", { raw, filename: f.name, page: parseInt(curPage()), rtCheck: ($("rtcheck") as HTMLInputElement).checked }, [raw]);
    const key = planKey(d.plan_name, curPage());
    const existing = plans.find((p) => p.key === key);
    const dispBlob = new Blob([d.png], { type: "image/png" });
    await savePlanBlobs(key, { disp: dispBlob, raw: new Blob([rawCopy]), filename: f.name });
    if (existing) {
      state = existing;
      if (!state.image_url) state.image_url = URL.createObjectURL(dispBlob);
    } else {
      state = { image_url: URL.createObjectURL(dispBlob), width: d.width, height: d.height,
        maengel: d.maengel || [], plan_name: d.plan_name, rooms: d.rooms || [], model: null, filename: f.name };
      upsertActivePlan();
    }
    activeNum = null; render();
    const dup = existing ? " · bereits analysiert" : "";
    const nDet = !existing && (d.maengel || []).length ? ` · ${d.maengel.length} RT-Hinweis(e) ohne KI` : "";
    const nR = (d.rooms || []).length;
    const nDb = d.db_count ? ` · Projekt-DB: ${d.db_count} Pläne` : "";
    $("vstatus").textContent = `Plan geladen${nR ? ` · ${nR} Raum/Räume` : ""}${nDet}${dup}${nDb}`;
    if (d.revision_info && (d.revision_info.aenderungen || []).length) {
      const a = d.revision_info.aenderungen;
      msg(`Neue Fassung erkannt (vorher: ${d.revision_info.vorher}, erfasst ${d.revision_info.erfasst}). ` +
        `Änderungen: ${a.slice(0, 5).join(" · ")}${a.length > 5 ? ` · … (${a.length - 5} weitere)` : ""}`, "warn");
    }
  } catch (e) {
    msg("Vorschau fehlgeschlagen: " + (e as Error).message, "err");
  } finally {
    $("status").textContent = "";
  }
}

/* ---------- Render ---------- */
function render() {
  const stage = $("stage");
  if (!state.image_url) return;
  const iw = state.width, ih = state.height;
  stage.className = "stage";
  stage.innerHTML = `<img id="plan" src="${state.image_url}" alt="Plan">`;
  for (const m of state.maengel) {
    const b = m.box || ({} as Box);
    if (b.x == null || b.w == null) continue;
    const c = SEV[m.schwere ?? ""] || SEV.gering;
    const div = document.createElement("div");
    div.className = "box";
    div.dataset.num = String(m.nummer); div.dataset.color = c;
    div.style.borderColor = c;
    div.style.left = (b.x / iw * 100) + "%";
    div.style.top = (b.y / ih * 100) + "%";
    div.style.width = (b.w / iw * 100) + "%";
    div.style.height = (b.h / ih * 100) + "%";
    div.innerHTML = `<span class="tag" style="background:${c}">#${m.nummer}</span>`;
    stage.appendChild(div);
  }
  const img = $("plan") as HTMLImageElement;
  const setup = () => { planeW = img.naturalWidth; planeH = img.naturalHeight; fitView(); requestAnimationFrame(fitView); };
  if (img.complete && img.naturalWidth) setup(); else img.onload = setup;
  renderRooms();

  $("panelTitle").textContent = `Identifizierte Mängel (${state.maengel.length})`;
  const list = $("list");
  list.innerHTML = state.maengel.length ? "" : '<span class="muted">Keine Mängel gefunden.</span>';
  state.maengel.forEach((m, i) => {
    const c = SEV[m.schwere ?? ""] || SEV.gering;
    const isRt = (m.gewerk || "").toLowerCase().includes("rt-abgleich");
    const isCross = (m.gewerk || "").toLowerCase() === "konsistenz";
    const el = document.createElement("div");
    el.className = "finding" + (isRt ? " rt" : "") + (isCross ? " cross" : "") + (m.bewertung === "richtig" ? " vOk" : m.bewertung === "falsch" ? " vNo" : "");
    el.dataset.num = String(m.nummer);
    el.innerHTML = `
      <div class="head">
        <span class="num" style="background:${c}">${m.nummer}</span>
        <h3 class="ftitle" data-act="${m.nummer}" title="Markierung im Plan hervorheben">${esc(m.titel)}</h3>
        ${m.klasse === "GEMESSEN"
          ? '<span class="klasse gemessen" title="Deterministisch aus Stempel-/CAD-Werten berechnet – belastbar">GEMESSEN</span>'
          : '<span class="klasse hinweis" title="KI-Bild-/Textinterpretation – im Plan zu verifizieren">KI-HINWEIS</span>'}
      </div>
      <span class="norm">#${m.katalog_nr ?? "?"} · ${esc(m.gewerk || "")} · ${esc(m.schwere || "")}${m.konfidenz ? " · Konf. " + esc(m.konfidenz) : ""}${m.raumnummer ? " · Raum " + esc(m.raumnummer) : ""}${m.norm ? " · " + esc(m.norm) : ""}</span>
      <p>${esc(m.beschreibung || "")}</p>
      <div class="fb">
        <div class="fbrow">
          <button class="fbbtn ok${m.bewertung === "richtig" ? " on" : ""}" data-vok="${i}" title="Befund bestätigt">✓ richtig</button>
          <button class="fbbtn no${m.bewertung === "falsch" ? " on" : ""}" data-vno="${i}" title="Fehlerkennung / nicht relevant">✗ falsch</button>
          <span class="fbhint">Rückmeldung fürs Lernen</span>
        </div>
        <textarea class="fbtext" data-fb="${i}" rows="2" placeholder="z. B. „Das ist ein Fenster – fälschlich erkannt, kein Türstopper erforderlich.“">${esc(m.rueckmeldung || "")}</textarea>
      </div>
      <div class="actions">
        <button data-poolar="${i}">Poolar</button>
        <button data-del="${i}">Löschen</button>
      </div>`;
    el.addEventListener("mouseenter", () => highlight(m.nummer!, true));
    el.addEventListener("mouseleave", () => highlight(m.nummer!, false));
    list.appendChild(el);
  });
  list.querySelectorAll<HTMLElement>(".ftitle").forEach((t) => (t.onclick = () => activate(+t.dataset.act!)));
  list.querySelectorAll<HTMLElement>("[data-poolar]").forEach((b) => (b.onclick = () => openPoolar(+b.dataset.poolar!)));
  list.querySelectorAll<HTMLElement>("[data-del]").forEach((b) =>
    (b.onclick = () => { state.maengel.splice(+b.dataset.del!, 1); renumber(); render(); }));
  list.querySelectorAll<HTMLTextAreaElement>("[data-fb]").forEach((t) =>
    (t.oninput = () => { state.maengel[+t.dataset.fb!].rueckmeldung = t.value; persist(); }));
  list.querySelectorAll<HTMLElement>("[data-vok]").forEach((b) => (b.onclick = () => setVerdict(+b.dataset.vok!, "richtig")));
  list.querySelectorAll<HTMLElement>("[data-vno]").forEach((b) => (b.onclick = () => setVerdict(+b.dataset.vno!, "falsch")));
  ($("export") as HTMLButtonElement).disabled = state.maengel.length === 0;
  ($("exportPdf") as HTMLButtonElement).disabled = state.maengel.length === 0;
  renderTabs();
  persist();
}

function renumber() { state.maengel.forEach((m, i) => (m.nummer = i + 1)); }

/* ---------- Räume: Poolar-ID direkt kopieren ---------- */
function roomLabel(r: Room): string {
  if (r.raumnummer) return r.poolar_id || r.raumnummer;
  return (r.raumtyp as string) || (r.raumname as string) || "";
}
function renderRooms() {
  const box = $("rooms");
  if (!state.image_url) { box.style.display = "none"; box.innerHTML = ""; return; }
  const rooms = (state.rooms || []).filter((r) => roomLabel(r));
  const planName = state.plan_name || "";
  const chips: string[] = [];
  rooms.forEach((r, i) => {
    const cl = r.raumnummer
      ? `<button class="rtlink" data-rt="${i}" title="Raum in der RT-Checkliste öffnen">Checkliste →</button>`
      : "";
    chips.push(`<div class="rchip"><code>${esc(roomLabel(r))}</code>` +
      `<button data-rcopy="${i}" title="Kopieren">📋 kopieren</button>${cl}</div>`);
  });
  if (planName)
    chips.push(`<div class="rchip planchip"><span class="rlabel">Plan</span><code>${esc(planName)}</code>` +
      `<button data-pcopy="1" title="Plannamen kopieren">📋 kopieren</button></div>`);
  box.style.display = "";
  box.innerHTML = `<div class="rooms-list">${chips.join("")}</div>`;
  const pbtn = box.querySelector<HTMLElement>("[data-pcopy]");
  if (pbtn) pbtn.onclick = () => copyRoom(planName, pbtn);
  const list = box.querySelectorAll<HTMLElement>("[data-rcopy]");
  rooms.forEach((r, i) => { list[i].onclick = () => copyRoom(roomLabel(r), list[i]); });
  box.querySelectorAll<HTMLElement>("[data-rt]").forEach((b) => {
    const r = rooms[+b.dataset.rt!];
    b.onclick = () => window.open(CHECKLIST_URL + "?raum=" + encodeURIComponent(r.raumnummer!), "_blank");
  });
}
function copyRoom(id: string, btn: HTMLElement) {
  void navigator.clipboard.writeText(id).then(() => {
    const prev = btn.innerHTML;
    btn.innerHTML = "kopiert ✓";
    setTimeout(() => { btn.innerHTML = prev; }, 1200);
  });
}
function highlight(num: number, on: boolean) {
  const el = document.querySelector(`.box[data-num="${num}"]`);
  if (el) el.classList.toggle("sel", on);
}
function activate(num: number) {
  activeNum = activeNum === num ? null : num;
  let activeEl: HTMLElement | null = null;
  document.querySelectorAll<HTMLElement>(".box").forEach((el) => {
    const on = +el.dataset.num! === activeNum;
    el.classList.toggle("active", on);
    el.style.background = on ? hexA(el.dataset.color!, 0.18) : "transparent";
    if (on) activeEl = el;
  });
  document.querySelectorAll<HTMLElement>(".finding").forEach((c) =>
    c.classList.toggle("cardActive", +c.dataset.num! === activeNum));
  if (activeEl) flyToBox(activeEl);
}
function setVerdict(i: number, v: string) {
  const m = state.maengel[i];
  m.bewertung = m.bewertung === v ? "" : v;
  render();
}

/* ---------- Poolar-Direktkopie ---------- */
function copyText(t: string) {
  void navigator.clipboard.writeText(t).then(() => {
    const el = document.getElementById("pMsg");
    if (el) { el.textContent = "kopiert ✓"; setTimeout(() => (el.textContent = ""), 1200); }
  });
}
const normRn = (s: string | undefined) => (s || "").replace(/\s+/g, "");
function openPoolar(i: number) {
  const m = state.maengel[i];
  const room = m.raumnummer
    ? (state.rooms || []).find((r) => r.raumnummer && normRn(r.raumnummer) === normRn(m.raumnummer))
    : null;
  const poolar = room ? roomLabel(room) : (m.raumnummer || "");
  const raumnr = room ? room.raumnummer : (m.raumnummer || "");
  const plan = state.plan_name || "";
  const fields: [string, string][] = [
    ["Typ", "Planprüfmangel Bau"],
    ["Raumnummer (Poolar)", poolar],
    ["Plan", plan],
    ["Hinweis Ort", plan && poolar ? `${plan} - ${poolar}` : (m.ort || "")],
    ["Kurzbezeichnung", m.titel || ""],
    ["Beschreibung", m.beschreibung || ""],
    ["Status", "offen"],
  ];
  const rows = fields.map(([label, val], idx) =>
    `<div class="prow"><label>${label}</label>` +
    (label === "Beschreibung"
      ? `<textarea id="pf${idx}" rows="4">${esc(val)}</textarea>`
      : `<input id="pf${idx}" value="${esc(val).replace(/"/g, "&quot;")}">`) +
    `<button data-copy="${idx}">Kopieren</button></div>`).join("");
  const clBtn = raumnr ? `<button id="pChecklist" title="Raum in der RT-Checkliste öffnen">Zur RT-Checkliste →</button>` : "";
  const ov = document.createElement("div");
  ov.className = "modal-ov";
  ov.innerHTML =
    `<div class="modal">
       <div class="modal-head"><b>Poolar-Felder · Mangel #${m.nummer}</b><button id="pClose">✕</button></div>
       ${rows}
       <div class="modal-foot"><button id="pAll">Alles kopieren</button>${clBtn}<span id="pMsg" class="muted"></span></div>
     </div>`;
  document.body.appendChild(ov);
  const val = (idx: number) => (document.getElementById("pf" + idx) as HTMLInputElement).value;
  ov.querySelectorAll<HTMLElement>("[data-copy]").forEach((b) => (b.onclick = () => copyText(val(+b.dataset.copy!))));
  document.getElementById("pAll")!.onclick = () =>
    copyText(fields.map(([l], idx) => `${l}: ${val(idx)}`).join("\n"));
  if (raumnr) document.getElementById("pChecklist")!.onclick = () =>
    window.open(CHECKLIST_URL + "?raum=" + encodeURIComponent(raumnr), "_blank");
  document.getElementById("pClose")!.onclick = () => ov.remove();
  ov.addEventListener("click", (e) => { if (e.target === ov) ov.remove(); });
}

/* ---------- Zoom & Pan (transform-basiert) ---------- */
function applyView(anim = false) {
  const st = $("stage"); if (!st) return;
  if (anim) { st.classList.add("anim"); setTimeout(() => st.classList.remove("anim"), 340); }
  st.style.transform = `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`;
  $("zoomLabel").textContent = Math.round(view.scale * 100) + "%";
}
function fitScale() {
  const vp = $("viewport");
  if (!planeW || !vp) return 1;
  return Math.min(vp.clientWidth / planeW, vp.clientHeight / planeH);
}
function clampScale(s: number) { const f = fitScale(); return Math.max(f * 0.8, Math.min(s, f * 24)); }
function fitView() {
  const vp = $("viewport"); if (!planeW || !vp) return;
  const s = fitScale();
  view = { scale: s, tx: (vp.clientWidth - s * planeW) / 2, ty: (vp.clientHeight - s * planeH) / 2 };
  applyView();
}
function zoomAt(lx: number, ly: number, factor: number) {
  const ns = clampScale(view.scale * factor);
  const k = ns / view.scale;
  view = { scale: ns, tx: lx - k * (lx - view.tx), ty: ly - k * (ly - view.ty) };
  applyView();
}
function zoomCenter(factor: number) {
  const vp = $("viewport"); zoomAt(vp.clientWidth / 2, vp.clientHeight / 2, factor);
}
function flyToBox(el: HTMLElement) {
  const vp = $("viewport"); if (!planeW) return;
  const bx = parseFloat(el.style.left) / 100 * planeW;
  const by = parseFloat(el.style.top) / 100 * planeH;
  const bw = parseFloat(el.style.width) / 100 * planeW;
  const bh = parseFloat(el.style.height) / 100 * planeH;
  const cx = bx + bw / 2, cy = by + bh / 2;
  const s = clampScale(Math.min(vp.clientWidth * 0.6 / bw, vp.clientHeight * 0.6 / bh));
  view = { scale: s, tx: vp.clientWidth / 2 - s * cx, ty: vp.clientHeight / 2 - s * cy };
  applyView(true);
}
$("zoomIn").onclick = () => zoomCenter(1.25);
$("zoomOut").onclick = () => zoomCenter(1 / 1.25);
$("zoomFit").onclick = () => fitView();
$("viewport").addEventListener("wheel", (e) => {
  e.preventDefault();
  const rect = $("viewport").getBoundingClientRect();
  zoomAt(e.clientX - rect.left, e.clientY - rect.top, Math.exp(-e.deltaY * 0.0015));
}, { passive: false });
window.addEventListener("keydown", (e) => {
  if (/^(INPUT|TEXTAREA)$/.test(document.activeElement?.tagName || "")) return;
  if (e.key === "+" || e.key === "=") zoomCenter(1.25);
  else if (e.key === "-" || e.key === "_") zoomCenter(1 / 1.25);
  else if (e.key === "0") fitView();
});
// Verschieben per Ziehen + Pinch-Zoom (Pointer Events → Maus + Touch/iPad)
(function () {
  const vp = $("viewport");
  const ptrs = new Map<number, { x: number; y: number }>();
  let pinchDist = 0;
  vp.addEventListener("pointerdown", (e) => {
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    vp.setPointerCapture(e.pointerId); vp.classList.add("grabbing");
    if (ptrs.size === 2) {
      const [a, b] = [...ptrs.values()];
      pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    }
  });
  vp.addEventListener("pointermove", (e) => {
    const p = ptrs.get(e.pointerId);
    if (!p) return;
    if (ptrs.size === 2) {
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const [a, b] = [...ptrs.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist > 0) {
        const rect = vp.getBoundingClientRect();
        zoomAt((a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top, d / pinchDist);
      }
      pinchDist = d;
      return;
    }
    view.tx += e.clientX - p.x; view.ty += e.clientY - p.y;
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    applyView();
  });
  const up = (e: PointerEvent) => { ptrs.delete(e.pointerId); pinchDist = 0; if (!ptrs.size) vp.classList.remove("grabbing"); };
  vp.addEventListener("pointerup", up);
  vp.addEventListener("pointercancel", up);
})();

/* ---------- Drag & Drop ---------- */
(function () {
  const vp = $("viewport");
  const stop = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
  ["dragenter", "dragover"].forEach((ev) =>
    vp.addEventListener(ev, (e) => { stop(e); (e as DragEvent).dataTransfer!.dropEffect = "copy"; vp.classList.add("dropping"); }));
  vp.addEventListener("dragleave", (e) => {
    stop(e);
    if (!vp.contains((e as DragEvent).relatedTarget as Node)) vp.classList.remove("dropping");
  });
  vp.addEventListener("drop", (e) => {
    stop(e); vp.classList.remove("dropping");
    const files = [...((e as DragEvent).dataTransfer?.files || [])].filter((f) => /\.(pdf|png|jpe?g)$/i.test(f.name));
    if (!files.length) { msg("Bitte PDF-, PNG- oder JPG-Datei(en) ablegen.", "err"); return; }
    const dt = new DataTransfer(); files.forEach((f) => dt.items.add(f));
    if (files.length === 1) { ($("file") as HTMLInputElement).files = dt.files; void loadPlan(); }
    else { ($("batchfile") as HTMLInputElement).files = dt.files; void submitBatch(); }
  });
})();

/* ---------- Export: Rückmeldungen → D1 (Format wie corrections/feedback.jsonl) ---------- */
async function exportCorr() {
  const ts = new Date().toISOString().slice(0, 19);
  const rows: Record<string, unknown>[] = [];
  for (const m of state.maengel) {
    const bewertung = (m.bewertung || "").trim();
    const rueck = (m.rueckmeldung || "").trim();
    if (!bewertung && !rueck) continue;
    rows.push({ ts, plan_name: state.plan_name, katalog_nr: m.katalog_nr, gewerk: m.gewerk,
      titel: m.titel, norm: m.norm, schwere: m.schwere, raumnummer: m.raumnummer,
      beschreibung: m.beschreibung, bewertung, rueckmeldung: rueck });
  }
  try {
    for (const row of rows) {
      const r = await fetch("/api/feedback", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(row) });
      if (!r.ok) throw new Error(String(r.status));
    }
    msg(rows.length ? `Gespeichert · ${rows.length} Rückmeldung(en) → zentrale Projekt-DB (Export: /api/feedback.jsonl)` :
      "Keine bewerteten/kommentierten Befunde — nichts zu speichern.", rows.length ? "warn" : "");
  } catch (e) {
    msg("Export fehlgeschlagen: " + (e as Error).message, "err");
  }
}

/* ---------- PDF-Export (client-seitig, pdf-lib) ---------- */
const SEV_RGB: Record<string, [number, number, number]> = {
  hoch: [0.898, 0.282, 0.302], mittel: [0.961, 0.651, 0.137], gering: [0.298, 0.431, 0.910],
};
async function exportPdf() {
  if (!state.image_url) return;
  msg("PDF wird erstellt …", "warn");
  try {
    const blobs = await idbGet<PlanBlobs>(blobKey(state.key ?? ""));
    const imgBytes = blobs?.disp ? new Uint8Array(await blobs.disp.arrayBuffer()) : null;
    const doc = await PDFDocument.create();
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const fontB = await doc.embedFont(StandardFonts.HelveticaBold);
    if (imgBytes) {
      const png = await doc.embedPng(imgBytes);
      const pw = 842, ph = pw * (png.height / png.width);
      const page = doc.addPage([pw, ph]);
      page.drawImage(png, { x: 0, y: 0, width: pw, height: ph });
      const iw = state.width, ih = state.height;
      for (const m of state.maengel) {
        const b = m.box;
        if (!b || b.x == null) continue;
        const [r, g, bl] = SEV_RGB[m.schwere ?? ""] ?? SEV_RGB.gering;
        const x = (b.x / iw) * pw, w = (b.w / iw) * pw;
        const yTop = (b.y / ih) * ph, h = (b.h / ih) * ph;
        page.drawRectangle({ x, y: ph - yTop - h, width: w, height: h, borderColor: rgb(r, g, bl), borderWidth: 1.4 });
        page.drawText(`#${m.nummer}`, { x: x + 2, y: ph - yTop + 3, size: 9, font: fontB, color: rgb(r, g, bl) });
      }
    }
    // Befundliste (A4, mit Umbruch)
    const W = 595, H = 842, margin = 40, size = 10.5, lh = size * 1.45;
    let page = doc.addPage([W, H]);
    let y = H - margin;
    const line = (txt: string, bold = false, color = rgb(0.12, 0.14, 0.19), sz = size) => {
      const f = bold ? fontB : font;
      const maxW = W - 2 * margin;
      const words = txt.split(" ");
      let cur = "";
      const flushLine = (t: string) => {
        if (y < margin + lh) { page = doc.addPage([W, H]); y = H - margin; }
        page.drawText(t, { x: margin, y: y - sz, size: sz, font: f, color });
        y -= lh;
      };
      for (const wd of words) {
        const cand = cur ? cur + " " + wd : wd;
        if (f.widthOfTextAtSize(cand, sz) > maxW && cur) { flushLine(cur); cur = wd; }
        else cur = cand;
      }
      if (cur) flushLine(cur);
    };
    line(`KI-Plancheck – Befundliste · ${state.plan_name}`, true, rgb(0.12, 0.14, 0.19), 14);
    line(`${state.maengel.length} Befunde · erstellt ${new Date().toLocaleString("de-DE")} · KI-gestützte Analyse, alle Befunde vor Verwendung prüfen`, false, rgb(0.42, 0.45, 0.5), 9);
    y -= lh * 0.5;
    for (const m of state.maengel) {
      const [r, g, bl] = SEV_RGB[m.schwere ?? ""] ?? SEV_RGB.gering;
      y -= lh * 0.4;
      line(`#${m.nummer} [${m.klasse === "GEMESSEN" ? "GEMESSEN" : "KI-HINWEIS"}] ${m.titel ?? ""}`, true, rgb(r, g, bl));
      line(`Katalog #${m.katalog_nr ?? "?"} · ${m.gewerk ?? ""} · ${m.schwere ?? ""}` +
        (m.konfidenz ? ` · Konfidenz ${m.konfidenz}` : "") + (m.raumnummer ? ` · Raum ${m.raumnummer}` : "") +
        (m.norm ? ` · ${m.norm}` : ""), false, rgb(0.42, 0.45, 0.5), 9);
      line(m.beschreibung ?? "");
      if (m.bewertung || m.rueckmeldung) line(`Bewertung: ${m.bewertung || "—"}${m.rueckmeldung ? ` · ${m.rueckmeldung}` : ""}`, false, rgb(0.42, 0.45, 0.5), 9);
    }
    const bytes = await doc.save();
    const u = URL.createObjectURL(new Blob([bytes as unknown as ArrayBuffer], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = u; a.download = `plancheck_${state.plan_name || "plan"}.pdf`; a.click();
    URL.revokeObjectURL(u); msg("", "");
  } catch (e) {
    msg("PDF-Export fehlgeschlagen: " + (e as Error).message, "err");
  }
}

/* ---------- Start ---------- */
$("go").onclick = () => void analyze();
$("batchgo").onclick = () => $("batchfile").click();
$("crossgo").onclick = () => void crossCheck();
$("batchfile").addEventListener("change", () => void submitBatch());
$("file").addEventListener("change", () => void loadPlan());
$("export").onclick = () => void exportCorr();
$("exportPdf").onclick = () => void exportPdf();
window.addEventListener("resize", () => { if (planeW) applyView(); });

void (async () => {
  await ensureLogin();
  await restore();
})();
